import React, { useState } from "react";
import { colors } from "../shared/constants";
import { inputStyle } from "../shared/styles";
import {
  LuFolderOpen,
  LuFolderInput,
  LuCopy,
  LuTrash2,
  LuLock,
  LuLockOpen,
  LuPencil,
} from "react-icons/lu";
import { fileIcon, formatBytes, formatDate } from "./helpers";

const TILE_CHROME = {
  borderRadius: "10px",
  padding: "20px 16px 16px",
  cursor: "pointer",
  transition: "all 0.15s",
  textAlign: "center",
};

function openDoc(doc) {
  const a = document.createElement("a");
  a.href = doc.file_url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function RenameInput({ initialValue, onCommit, onCancel }) {
  const [draft, setDraft] = useState(initialValue);
  return (
    <input
      autoFocus
      value={draft}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(draft);
        if (e.key === "Escape") onCancel();
      }}
      onBlur={() => onCommit(draft)}
      style={{ ...inputStyle, padding: "4px 8px", fontSize: "13px", textAlign: "center" }}
    />
  );
}

function FolderTile({ folder, isHovered, isRenaming, onOpen, onHover, onLeave, onStartRename, onCommitRename, onCancelRename, onDelete, onMove }) {
  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => onHover(folder.id)}
      onMouseLeave={onLeave}
    >
      <div
        onDoubleClick={() => !isRenaming && onOpen(folder)}
        style={{
          ...TILE_CHROME,
          backgroundColor: isHovered ? "#f0f4ff" : colors.white,
          border: `1px solid ${isHovered ? colors.blue : colors.border}`,
        }}
      >
        <LuFolderOpen size={36} style={{ color: "#C8922A", marginBottom: "10px" }} />
        {isRenaming ? (
          <RenameInput
            initialValue={folder.name}
            onCommit={(v) => onCommitRename(folder, v)}
            onCancel={onCancelRename}
          />
        ) : (
          <div style={{ fontWeight: "600", fontSize: "13px", color: colors.text, marginBottom: "4px" }}>
            {folder.name}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "20px", backgroundColor: colors.lightGrey, color: colors.slate }}>
            {folder.document_count} file{folder.document_count !== 1 ? "s" : ""}
            {folder.subfolder_count > 0 ? ` · ${folder.subfolder_count} folder${folder.subfolder_count !== 1 ? "s" : ""}` : ""}
          </span>
          {folder.is_system && <LuLock size={11} style={{ color: colors.slate }} title="System folder" />}
        </div>
      </div>
      {isHovered && !isRenaming && (
        <div style={{ position: "absolute", top: "8px", right: "8px", display: "flex", gap: "4px" }}>
          {!folder.is_system && (
            <button
              onClick={(e) => { e.stopPropagation(); onStartRename(folder.id); }}
              style={{ background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "6px", padding: "4px 6px", cursor: "pointer", color: colors.blue, lineHeight: 1 }}
            >
              <LuPencil size={13} />
            </button>
          )}
          <button
            title="Move to folder"
            onClick={(e) => { e.stopPropagation(); onMove(folder); }}
            style={{ background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "6px", padding: "4px 6px", cursor: "pointer", color: colors.slate, lineHeight: 1 }}
          >
            <LuFolderInput size={13} />
          </button>
          {!folder.is_system && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(folder.id); }}
              style={{ background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "6px", padding: "4px 6px", cursor: "pointer", color: colors.red, lineHeight: 1 }}
            >
              <LuTrash2 size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FileTile({ doc, isHovered, isRenaming, userRole, onHover, onLeave, onStartRename, onCommitRename, onCancelRename, onToggleLock, onMove, onCopy, onDelete }) {
  return (
    <div
      onDoubleClick={() => !isRenaming && openDoc(doc)}
      onMouseEnter={() => onHover(doc.id)}
      onMouseLeave={onLeave}
      style={{
        ...TILE_CHROME,
        backgroundColor: isHovered ? "#f0f4ff" : colors.white,
        border: `1px solid ${isHovered ? colors.blue : colors.border}`,
      }}
    >
      <div style={{ marginBottom: "10px" }}>{fileIcon(doc.name, 36)}</div>
      {isRenaming ? (
        <RenameInput
          initialValue={doc.name}
          onCommit={(v) => onCommitRename(doc, v)}
          onCancel={onCancelRename}
        />
      ) : (
        <div
          title={doc.name}
          style={{
            color: colors.text, fontWeight: "600", fontSize: "13px",
            marginBottom: "4px", overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {doc.name}
        </div>
      )}
      <div style={{ fontSize: "11px", color: colors.slate, marginBottom: "8px" }}>
        {formatBytes(doc.file_size)} {doc.file_size ? "·" : ""} {formatDate(doc.uploaded_at)}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", opacity: isHovered ? 1 : 0, transition: "opacity 0.15s" }}>
        {userRole !== "field_tech" && (
          <button
            title={doc.locked ? "Unlock document" : "Lock document (prevent moving/deleting)"}
            onClick={() => onToggleLock(doc)}
            style={{ background: "none", border: "none", cursor: "pointer", color: doc.locked ? colors.amber : colors.slate, padding: "4px", lineHeight: 1, opacity: doc.locked ? 1 : undefined }}
          >
            {doc.locked ? <LuLock size={14} /> : <LuLockOpen size={14} />}
          </button>
        )}
        {!doc.locked && (
          <button title="Rename" onClick={() => onStartRename(doc.id)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}>
            <LuPencil size={14} />
          </button>
        )}
        {!doc.locked && (
          <button title="Move to folder" onClick={() => onMove(doc)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}>
            <LuFolderInput size={14} />
          </button>
        )}
        <button title="Copy to folder" onClick={() => onCopy(doc)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}>
          <LuCopy size={14} />
        </button>
        {!doc.locked && (
          <button title="Delete" onClick={() => onDelete(doc.id)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}>
            <LuTrash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export function FolderContentsGrid({
  folders, documents, userRole,
  onOpenFolder, onDeleteFolder, onRenameFolder, onMoveFolder,
  onToggleLock, onMoveDoc, onCopyDoc, onDeleteDoc, onRenameDoc,
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);

  const commitFolderRename = (folder, draft) => {
    setRenamingId(null);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === folder.name) return;
    onRenameFolder(folder, trimmed);
  };

  const commitDocRename = (doc, draft) => {
    setRenamingId(null);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === doc.name) return;
    onRenameDoc(doc, trimmed);
  };

  if (folders.length === 0 && documents.length === 0) return null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px", padding: "20px" }}>
      {folders.map((folder) => (
        <FolderTile
          key={`f-${folder.id}`}
          folder={folder}
          isHovered={hoveredId === `f-${folder.id}`}
          isRenaming={renamingId === `f-${folder.id}`}
          onOpen={onOpenFolder}
          onHover={(id) => setHoveredId(`f-${id}`)}
          onLeave={() => setHoveredId(null)}
          onStartRename={(id) => setRenamingId(`f-${id}`)}
          onCommitRename={commitFolderRename}
          onCancelRename={() => setRenamingId(null)}
          onDelete={onDeleteFolder}
          onMove={onMoveFolder}
        />
      ))}
      {documents.map((doc) => (
        <FileTile
          key={`d-${doc.id}`}
          doc={doc}
          userRole={userRole}
          isHovered={hoveredId === `d-${doc.id}`}
          isRenaming={renamingId === `d-${doc.id}`}
          onHover={(id) => setHoveredId(`d-${id}`)}
          onLeave={() => setHoveredId(null)}
          onStartRename={(id) => setRenamingId(`d-${id}`)}
          onCommitRename={commitDocRename}
          onCancelRename={() => setRenamingId(null)}
          onToggleLock={onToggleLock}
          onMove={onMoveDoc}
          onCopy={onCopyDoc}
          onDelete={onDeleteDoc}
        />
      ))}
    </div>
  );
}
